import { observer } from 'mobx-react-lite';
import { FieldArrayWithId, UseFormReturn, useWatch } from 'react-hook-form';
import { UseTrackedFieldArrayRemove } from '../../../../forms/use-tracked-field-array';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import React, { useEffect, useMemo } from 'react';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features/use-enabled-features';
import { useDrawerControls } from '../../../../drawers/components/drawer-context-provider';
import { EventNotificationsEntityIdProvider } from '../../../../event-notifications/event-notifications-entity-id-context';
import { Box, cssVar, FormControl, FormGroup, Button, FormRow, modals } from '@cad-arc';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { CancelServiceRotationModal } from '../../../cancel-service-rotation-modal';
import { onTowRotationCancellation } from '../../helpers/vehicle-helpers';
import { FormLocationSelect } from '../../../../forms/integrated-form-controls/form-location-select';
import { GenerateTowReportButton } from '../../generate-tow-report-button';
import { TowVehicleBaseKey } from '../types';
import { useApplicationType } from '../../../../common/hooks/use-application-type';
import { EntityPanelFormVehicleCardControls } from '../../../../panels/entity-panels/forms/vehicles/entity-panel-form-vehicle-card-controls';
import { EntityPanelFormBaseVehicleInputs } from '../../../../panels/entity-panels/forms/vehicles/entity-panel-form-vehicle-base-inputs';
import { OtherField } from '../../../../panels/entity-panels/forms/vehicles/entity-panel-form-vehicles-other-field';
import { TowVehicleHoldSelect } from './event-panel-form-tow-vehicle-hold-select';

export const TowVehicleFields = observer(function TowVehicleFields(props: {
    form: UseFormReturn<EventPanelFormType>;
    index: number;
    field: FieldArrayWithId<EventPanelFormType, 'vehicles', 'rhfKey'>;
    remove: UseTrackedFieldArrayRemove;
    eventPanelOptions: EventPanelOptions;
    allowMembersControl?: boolean;
    baseKey: TowVehicleBaseKey;
    eventId: number | null;
    eventNumber?: string;
    isReadOnly?: boolean;
    userManuallyAddedField?: boolean;
}): React.ReactElement {
    const {
        form: eventForm,
        index,
        eventPanelOptions,
        remove,
        baseKey,
        eventId,
        eventNumber,
        isReadOnly,
        userManuallyAddedField,
    } = props;

    const { isFirstResponder } = useApplicationType();
    const { rotationAccess, enhancedServiceRotation } = useEnabledFeatures();
    const drawerControls = useDrawerControls();
    const isNewEvent = !eventId;

    const vehicleId = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.id`,
    });
    const vehicleContextId = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.contextId`,
    });
    const isPersisted = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.isPersisted`,
    });
    const isDepartmentTow =
        useWatch({
            control: eventForm.control,
            name: `${props.baseKey}.${index}.towTypeOption`,
        })?.value === 'Department';
    const showNcicTowVehicleButton = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.showNcicTowVehicleButton`,
    });
    const rotationServiceListPositionId = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.rotationServiceListPositionId`,
    });
    const towCompanyNumberValue = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.towCompanyPhoneNumber`,
    });
    const isPrivateTow =
        useWatch({
            control: eventForm.control,
            name: `${props.baseKey}.${index}.towTypeOption`,
        })?.value === 'Private';

    const towVehicleIsHeld = useWatch({
        control: eventForm.control,
        name: `${props.baseKey}.${index}.isHeld`,
    });
    const towVehicleHoldValue = towVehicleIsHeld;

    const isTowCompanyChanged = eventForm.getFieldState(
        `${props.baseKey}.${index}.towCompanyOption`
    )?.isTouched;
    const isTowCompanyPhoneNumberVisible =
        isPersisted && towCompanyNumberValue && !isTowCompanyChanged;

    const showTowVehicleHoldStatusSelect =
        !!vehicleId && enhancedServiceRotation && props.baseKey === 'towVehicles';

    const areTowFieldsReadOnly = useMemo(() => {
        if (isReadOnly) {
            return true;
        }
        if (isFirstResponder && isNewEvent) {
            return false;
        }
        return rotationAccess && isDepartmentTow;
    }, [isReadOnly, isFirstResponder, isNewEvent, isDepartmentTow, rotationAccess]);

    useEffect(() => {
        const subscription = props.form.watch((data, { name, type }) => {
            if (
                rotationAccess &&
                eventId &&
                type === 'change' &&
                props.baseKey === 'towVehicles' &&
                name === `${props.baseKey}.${index}.towTypeOption` &&
                data?.towVehicles?.[index]?.towTypeOption?.value === 'Department'
            ) {
                drawerControls.open({
                    type: 'service-rotation',
                    props: {
                        eventId,
                        towRotationProps: {
                            eventForm: props.form,
                            towVehicleBaseKey: props.baseKey,
                            towVehicleIndex: index,
                        },
                    },
                });
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [drawerControls, eventId, index, props.baseKey, props.form, rotationAccess]);

    return (
        <EventNotificationsEntityIdProvider entityId={vehicleId ?? undefined}>
            <EntityPanelFormVehicleCardControls
                remove={remove}
                isNewEntity={isNewEvent}
                allowMembersControl={props.allowMembersControl}
                baseKey={baseKey}
                index={index}
                isReadOnly={isReadOnly}
                entityNumber={eventNumber}
                eventId={eventId ?? undefined}
            />
            <FormGroup spacing={1}>
                {showTowVehicleHoldStatusSelect && (
                    <FormRow>
                        <FormControl label="Status">
                            <Box w="125px">
                                <TowVehicleHoldSelect
                                    value={towVehicleHoldValue}
                                    options={eventPanelOptions.towVehicleHoldOptions ?? []}
                                    towVehicleId={vehicleContextId}
                                    onConfirmChange={(nextOption) => {
                                        eventForm.setValue(
                                            `${props.baseKey}.${index}.isHeld`,
                                            nextOption.value,
                                            { shouldDirty: false, shouldValidate: false }
                                        );
                                        eventForm.setValue(
                                            `${props.baseKey}.${index}.isHeld`,
                                            nextOption.value,
                                            { shouldDirty: false, shouldValidate: false }
                                        );
                                    }}
                                />
                            </Box>
                        </FormControl>
                    </FormRow>
                )}
                <EntityPanelFormBaseVehicleInputs
                    index={index}
                    baseKey={baseKey}
                    entityPanelOptions={eventPanelOptions}
                    isReadOnly={isReadOnly}
                    userManuallyAddedField={userManuallyAddedField}
                />

                <FormGroup>
                    <FormRow>
                        <FormSelect
                            control={eventForm.control}
                            label="Type"
                            options={eventPanelOptions.towTypeOptions}
                            name={`${props.baseKey}.${index}.towTypeOption`}
                            asEditable={!!isPersisted}
                            length="md"
                            showRequiredIndicator
                            portalToToastContainer
                            mode={isReadOnly ? 'read-only' : undefined}
                        />
                        <FormSelect
                            control={eventForm.control}
                            label="Tow Company Name"
                            name={`${props.baseKey}.${index}.towCompanyOption`}
                            options={eventPanelOptions.towCompanyOptions}
                            asEditable={!!isPersisted}
                            length="md"
                            mode={areTowFieldsReadOnly ? 'read-only' : undefined}
                            portalToToastContainer
                            onChange={() => {
                                eventForm.resetField(
                                    `${props.baseKey}.${index}.towCompanyPhoneNumber`
                                );
                            }}
                        />
                    </FormRow>
                    {isTowCompanyPhoneNumberVisible && (
                        <FormRow>
                            <FormInput
                                length="md"
                                mode="read-only"
                                label="Tow Company Phone"
                                name={`${props.baseKey}.${index}.towCompanyPhoneNumber`}
                                control={eventForm.control}
                            />
                        </FormRow>
                    )}
                    <OtherField
                        watchPath={`${props.baseKey}.${index}.towCompanyOption`}
                        otherPath={`${props.baseKey}.${index}.towCompanyOther`}
                    >
                        <FormRow hasIndent>
                            <FormInput
                                control={eventForm.control}
                                label="Tow Company"
                                name={`${props.baseKey}.${index}.towCompanyOther`}
                                asEditable={!!isPersisted}
                                length="md"
                                mode={isReadOnly ? 'read-only' : undefined}
                            />
                        </FormRow>
                    </OtherField>
                </FormGroup>
                <FormRow>
                    <FormLocationSelect
                        control={eventForm.control}
                        label="Towed From"
                        name={`${props.baseKey}.${index}.towedFromLocation`}
                        showRequiredIndicator={true}
                        asEditable={!!isPersisted}
                        placeInputOutside
                        length="md"
                        mode={isReadOnly ? 'read-only' : undefined}
                    />

                    <FormLocationSelect
                        control={eventForm.control}
                        label="Towed To"
                        name={`${props.baseKey}.${index}.towedToLocation`}
                        showRequiredIndicator={!isPrivateTow}
                        asEditable={!!isPersisted}
                        length="md"
                        placeInputOutside
                        mode={areTowFieldsReadOnly ? 'read-only' : undefined}
                    />
                </FormRow>
                {rotationServiceListPositionId && !isReadOnly && (
                    <Button
                        style={{
                            marginTop: cssVar('arc.space.1'),
                            alignSelf: 'flex-start',
                        }}
                        onClick={() => {
                            modals.blank({
                                children: ({ onClose }) => (
                                    <CancelServiceRotationModal
                                        serviceTypeName="Tow"
                                        onClose={() => {
                                            onClose();
                                            onTowRotationCancellation({
                                                eventForm,
                                                baseKey: props.baseKey,
                                                index,
                                                openDrawer: drawerControls.open,
                                            });
                                        }}
                                        id={rotationServiceListPositionId}
                                    />
                                ),
                            });
                        }}
                        variant="ghost"
                        size="sm"
                    >
                        Cancel Tow Service Request
                    </Button>
                )}
            </FormGroup>
            {showNcicTowVehicleButton &&
                isPersisted &&
                vehicleContextId &&
                eventId &&
                isDepartmentTow &&
                !isReadOnly && (
                    <GenerateTowReportButton
                        towVehicleId={vehicleContextId}
                        agencyEventId={eventId}
                    />
                )}
        </EventNotificationsEntityIdProvider>
    );
});
